import { useState } from 'react';

export interface FoldingSidebarState {
  /** The desktop sidebar's open state, for `SidebarProvider`'s `open`. */
  readonly open: boolean;
  /** For `SidebarProvider`'s `onOpenChange`: a toggle by the user. */
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * Controls the desktop sidebar so it shows folded to its icon rail while
 * `folded` holds and the way the user left it otherwise. A toggle while
 * folded is the user's and stands until `folded` ends; the sidebar is then
 * back as it was before.
 *
 * The fold is derived during render rather than applied to the sidebar after
 * mount, so a page that opens folded (a result, loaded or reloaded by URL)
 * paints folded in its first frame instead of sliding the whole page 200px
 * sideways once it has loaded (#921).
 */
export function useFoldingSidebar(folded: boolean): FoldingSidebarState {
  const [open, setOpen] = useState(true);
  const [openWhileFolded, setOpenWhileFolded] = useState(false);
  const [wasFolded, setWasFolded] = useState(folded);
  // Every fold starts folded, whatever a toggle did during the last one.
  if (folded !== wasFolded) {
    setWasFolded(folded);
    setOpenWhileFolded(false);
  }
  return folded
    ? { open: openWhileFolded, onOpenChange: setOpenWhileFolded }
    : { open, onOpenChange: setOpen };
}
